import { join } from 'path';

import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import { File, getFilesAndFolders } from '@/utils/posts';
import {
  EmptyPage,
  FAQPage,
  getPathArray,
  PostContent,
  PostsList,
  PostType,
  PostWithPath,
  SectionType,
} from '@visalex/help-center';

import { GetStaticProps } from 'next';
import { Router, useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { returnPost } from '../api/post/[...slug]';

import styles from '@/components/Help/FAQ.module.scss';

const Post: FunctionComponent<{ post: PostType; section: SectionType; listOfFaq: PostWithPath[] }> = ({
  post,
  section,
  listOfFaq,
}) => {
  const { t } = useTranslation();
  const router = useRouter();

  const [currentPath, setCurrentPath] = useState('');

  /**
   *  Scrolling up on component mount
   */
  React.useEffect(() => {
    Router.events.on('routeChangeComplete', () => {
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    });
  }, []);

  const pageTitle = section && section.title ? section.title : post && post.title ? post.title : t('faq.seo-title');
  const pageDescription =
    section && section.title ? section.title : post && post.description ? post.description : t('faq.seo-description');

  /**
   * Handler on post click. Redirects to a new route which is specified inside the post
   * @param e - event
   * @param url - post's url
   */
  const handlePostClick = (e: React.MouseEvent<HTMLDivElement>, url: string) => {
    e.preventDefault();
    router.push(`${router.asPath}/${url}`);
  };

  /**
   * Get array of breadcrumbs based on current path
   */
  const breadcrumbs = useMemo(() => {
    if (router) {
      const linkPath = router.asPath.split('/');

      return getPathArray(linkPath);
    }

    return [];
  }, [router]);

  useEffect(() => {
    if (router) {
      const linkPath = router.asPath.replace('/help', '');

      setCurrentPath(linkPath);
    }
  }, [router]);

  let pageContent = null;

  /**
   * Depending on type of page (section of post) choose the correspondent page content
   */
  if (section) {
    pageContent =
      section && (section.sections?.length || section.posts?.length) ? (
        <PostsList
          sectionsList={section.sections ? section.sections : []}
          postsList={section.posts ? section.posts : []}
          handleClick={handlePostClick}
        />
      ) : (
        <EmptyPage />
      );
  } else {
    pageContent = post && post.content ? <PostContent post={post} /> : null;
  }

  return (
    <Layout title={pageTitle} description={pageDescription}>
      <div className={styles.faq__article}>
        {listOfFaq && listOfFaq.length > 0 ? (
          <FAQPage listOfFaq={listOfFaq} showBreadCrumbs breadcrumbs={breadcrumbs} currentPath={currentPath}>
            {pageContent}
          </FAQPage>
        ) : (
          <EmptyPage />
        )}
      </div>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const slug = Array.isArray(params?.slug) ? params?.slug.join('/') : (params?.slug as string);

  /**
   * Getting all posts, sections, faq list
   */
  if (slug) {
    const { section, post, listOfFaq } = await returnPost(['marketing', /*locale || */ 'en', slug]);

    if (section) {
      return {
        props: {
          section,
          listOfFaq,
          ...(await serverSideTranslations(locale!)),
        },
      };
    }

    return {
      props: {
        post,
        listOfFaq,
        ...(await serverSideTranslations(locale!)),
      },
    };
  }

  return { props: { listOfFaq: [], ...(await serverSideTranslations(locale!)) } };
};

export const getStaticPaths = async ({ locale }: { locale: string }) => {
  const languages = ['en' /*, 'es', 'pt'*/];

  type Path = {
    params: {
      slug: string[];
    };
    locale: string;
  };

  let paths: Path[] = [];

  for (const language of languages) {
    const postsDirectory = join(process.cwd(), `./public/posts/marketing/${language}`);
    const langPaths = await getFilesAndFolders(postsDirectory);

    paths = paths.concat(
      langPaths.map((item: File) => ({
        params: {
          slug: item.path.substring(1).split('/'),
        },
        locale: language,
      }))
    );
  }

  return {
    paths,
    fallback: true,
  };
};

export default Post;
